import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { message, notification } from 'antd';
import { AxiosError } from 'axios';
import { saveAs } from 'file-saver';

import { getReportInvoice, getInvoiceReportDownload } from 'services/reports';
import { getDebitors } from 'services/partners';
import useTranslation from 'customHooks/translate';
import { PARTNERS_DATA, REPORTS_INCOMES } from 'events/types';

import { ReportsTab } from './components/ReportsTab';
import { ReportsMultiSelect } from './components/reportsMultiSelect';

export const Invoices = () => {
  const translation = useTranslation();

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [partner, setPartner] = useState<any>(null);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [confirmedFromDate, setConfirmedFromDate] = useState('');
  const [confirmedToDate, setConfirmedToDate] = useState('');
  const [confirmedPartner, setConfirmedPartner] = useState<any>(null);

  const setDateRange = (from: string, to: string) => {
    setFromDate(from);
    setToDate(to);
  };
  const handleSelectPartners = (values: number[]) => {
    setPartner(values);
  };
  const openNotification = (message, type = 'warning') => {
    notification[type]({
      message,
      duration: 2,
    });
  };
  const { data: debitorsData } = useQuery<any, AxiosError, any[]>({
    queryKey: [PARTNERS_DATA],
    queryFn: () => getDebitors(),
    select: (response) =>
      response.data.data.map((debitor) => ({
        ...debitor,
        label: debitor.name,
        value: debitor.id,
      })),
  });

  const { data: invoiceZipQueueId } = useQuery({
    queryKey: [
      REPORTS_INCOMES,
      confirmedFromDate,
      confirmedToDate,
      confirmedPartner,
    ],
    queryFn: () =>
      getReportInvoice(
        confirmedFromDate,
        confirmedToDate,
        true,
        confirmedPartner
      ),
    select: (res) => res.data,
    enabled: shouldFetch,
    onSuccess: (data) => {
      if (data?.length === 0) {
        openNotification(
          'An error occurred while generating the download link'
        );
      } else {
        openNotification('Ready for download', 'success');
      }
      setShouldFetch(false);
    },
  });

  const handleConfirm = () => {
    if (!fromDate || !toDate || partner === null) {
      openNotification(`${translation.reports.Parameters}`, 'error');
      return;
    }
    setConfirmedFromDate(fromDate);
    setConfirmedToDate(toDate);
    setConfirmedPartner(partner);
    setShouldFetch(true);
  };

  const handleDownload = async (invoiceZipQueueId) => {
    try {
      const response = await getInvoiceReportDownload(invoiceZipQueueId);
      const blob = new Blob([response.data], { type: 'application/zip' });
      saveAs(blob, `invoice_${invoiceZipQueueId}.zip`);
      message.success(translation.messages.downloadStart);
    } catch (error) {
      message.error('An error occurred while downloading the file');
    }
  };

  return (
    <div className="flex flex-col gap-[20px] p-[16px] md:w-[80%] md:p-[32px] xl:w-[50%]">
      <ReportsTab setDateRange={setDateRange} />
      <ReportsMultiSelect
        title={translation.subusers.debitor}
        onChange={handleSelectPartners}
        options={debitorsData || []}
      />
      <div className="flex gap-[12px]">
        <button
          onClick={handleConfirm}
          className=" w-full rounded-[6px] bg-[#466afb] px-[24px] py-[12px] text-[14px] font-normal leading-[16px] text-white md:text-[16px]"
        >
          {translation.reports.BtnConfirm}
        </button>
        {!!invoiceZipQueueId && (
          <button
            onClick={() => handleDownload(invoiceZipQueueId)}
            className="flex w-full items-center justify-center gap-[8px] rounded-[6px] rounded-[6px] border-2 border-[#CECECE] px-[16px] py-[8px] text-[14px] font-normal leading-[16px] text-black transition-all duration-300 ease-in-out hover:bg-gray-100 md:px-[24px] md:py-[12px] md:text-[16px]"
          >
            <img
              className="mr-[5px] h-5 w-5"
              src="./assets/save-folder-icon.png"
              alt=""
            />
            {translation.reports.Download}
          </button>
        )}
      </div>
    </div>
  );
};
